import { Button, Input, notification, Popover } from 'antd';
import { PureComponent, ReactNode } from 'react';
import { track } from '../../../../../tracking';
import { PAD_V2_CONTAINER } from '../PadContainer';

type Props = {
    onSave: (name: string) => void;
    onReset: () => void;
    defaultName: string;
    originalName: string;
    isNameEdited: boolean;
};

type State = {
    open: boolean;
    drugName: string;
};

class RenameMedName extends PureComponent<Props, State> {
    state: State = {
        open: false,
        drugName: this.props.defaultName,
    };

    handleOpenChange = (newOpen: boolean): void => {
        this.setState({ open: newOpen });
    };

    hide = (): void => {
        this.setState({ open: false });
    };

    onSave = (): void => {
        const { onSave } = this.props;
        const { drugName } = this.state;

        if (!drugName.trim()) {
            notification.error({
                message: 'Drug name cannot be empty',
                duration: 5,
            });
            return;
        }
        onSave(drugName.trim());
        this.hide();
        track('EDIT_MED_NAME_SAVE');
    };

    onReset = (): void => {
        const { onReset, originalName } = this.props;

        this.setState({ drugName: originalName });
        onReset();
        this.hide();
        track('EDIT_MED_NAME_RESET');
    };

    onDrugNameChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
        this.setState({ drugName: e.target.value });
    };

    render(): ReactNode {
        const { open, drugName } = this.state;
        const { isNameEdited, onReset } = this.props;

        return (
            <Popover
                content={
                    <div>
                        <Input
                            placeholder="Drug name"
                            value={drugName}
                            onChange={this.onDrugNameChange}
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                            }}
                        />
                        <div className="flex justify-end mt-8 space-x-16">
                            <Button onClick={this.onReset} type="default">
                                Reset
                            </Button>
                            <Button
                                onClick={this.onSave}
                                type="primary"
                                disabled={!drugName.trim()}
                            >
                                Save
                            </Button>
                        </div>
                    </div>
                }
                title="Rename drug name"
                trigger="click"
                visible={open}
                onVisibleChange={this.handleOpenChange}
                getPopupContainer={(triggerNode) =>
                    document.getElementById(PAD_V2_CONTAINER) || document.body
                }
                placement="bottomLeft"
                destroyTooltipOnHide
            >
                <div className="absolute bottom-0 left-0">
                    <div
                        className={`w-20 h-20 ${
                            isNameEdited ? 'bg-actionPrimary' : 'bg-neutral-9'
                        } min-w-20 min-h-20 flex items-center justify-center cursor-pointer tapering-shortform`}
                    >
                        <svg
                            width="10"
                            height="11"
                            viewBox="0 0 10 11"
                            className={`w-14 h-14 ${
                                isNameEdited ? 'fill-white' : 'fill-neutral-5'
                            }`}
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path d="M9.19922 0.679688C8.76953 0.25 8.08594 0.25 7.65625 0.679688L7.07031 1.26562L8.98438 3.17969L9.57031 2.59375C10 2.16406 10 1.48047 9.57031 1.05078L9.19922 0.679688ZM3.35938 4.97656C3.24219 5.09375 3.14453 5.25 3.08594 5.40625L2.51953 7.14453C2.46094 7.30078 2.5 7.49609 2.63672 7.61328C2.75391 7.75 2.94922 7.78906 3.10547 7.73047L4.84375 7.16406C5 7.10547 5.15625 7.00781 5.27344 6.89062L8.53516 3.62891L6.62109 1.71484L3.35938 4.97656ZM1.875 1.5C0.839844 1.5 0 2.33984 0 3.375V8.375C0 9.41016 0.839844 10.25 1.875 10.25H6.875C7.91016 10.25 8.75 9.41016 8.75 8.375V6.5C8.75 6.16797 8.45703 5.875 8.125 5.875C7.77344 5.875 7.5 6.16797 7.5 6.5V8.375C7.5 8.72656 7.20703 9 6.875 9H1.875C1.52344 9 1.25 8.72656 1.25 8.375V3.375C1.25 3.04297 1.52344 2.75 1.875 2.75H3.75C4.08203 2.75 4.375 2.47656 4.375 2.125C4.375 1.79297 4.08203 1.5 3.75 1.5H1.875Z" />
                        </svg>
                    </div>
                    <div className="space-x-4 flex items-center bg-neutral-8 px-8 tapering-longform cursor-pointer">
                        <svg
                            width="10"
                            height="11"
                            viewBox="0 0 10 11"
                            className="w-14 h-14 fill-actionPrimary"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path d="M9.19922 0.679688C8.76953 0.25 8.08594 0.25 7.65625 0.679688L7.07031 1.26562L8.98438 3.17969L9.57031 2.59375C10 2.16406 10 1.48047 9.57031 1.05078L9.19922 0.679688ZM3.35938 4.97656C3.24219 5.09375 3.14453 5.25 3.08594 5.40625L2.51953 7.14453C2.46094 7.30078 2.5 7.49609 2.63672 7.61328C2.75391 7.75 2.94922 7.78906 3.10547 7.73047L4.84375 7.16406C5 7.10547 5.15625 7.00781 5.27344 6.89062L8.53516 3.62891L6.62109 1.71484L3.35938 4.97656ZM1.875 1.5C0.839844 1.5 0 2.33984 0 3.375V8.375C0 9.41016 0.839844 10.25 1.875 10.25H6.875C7.91016 10.25 8.75 9.41016 8.75 8.375V6.5C8.75 6.16797 8.45703 5.875 8.125 5.875C7.77344 5.875 7.5 6.16797 7.5 6.5V8.375C7.5 8.72656 7.20703 9 6.875 9H1.875C1.52344 9 1.25 8.72656 1.25 8.375V3.375C1.25 3.04297 1.52344 2.75 1.875 2.75H3.75C4.08203 2.75 4.375 2.47656 4.375 2.125C4.375 1.79297 4.08203 1.5 3.75 1.5H1.875Z" />
                        </svg>

                        <p className="text-actionPrimary font-700 text-12">Edit Name</p>
                    </div>
                </div>
            </Popover>
        );
    }
}

export default RenameMedName;
